'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { SidebarInvite } from '#pplx/frontend/apps/ask/layout-aux/sidebar-bottom/SidebarInvite.js';
import { SidebarPlans } from '#pplx/frontend/apps/ask/layout-aux/sidebar-bottom/SidebarPlans.js';
import { useShowUpgradeButton } from '#pplx/frontend/apps/ask/layout-aux/sidebar-bottom/useShowUpgradeButton.js';
import { useSidebarInviteKind } from '#pplx/frontend/apps/ask/layout-aux/sidebar-bottom/useSidebarInviteKind.js';
import { SidebarDesktop as SidebarDesktopComponent } from '#pplx/frontend/apps/ask/layout/sidebar/core/desktop/SidebarDesktop.js';
import { useSidebarInternalSettingsMenu } from '#pplx/frontend/apps/ask/layout/sidebar/core/shared/useSidebarInternalSettingsMenu.js';
import { useSidebarPinned } from '#pplx/frontend/apps/ask/layout/sidebar/core/shared/useSidebarPinned.js';
import { useUserCapabilities } from '#pplx/frontend/apps/ask/queries/user-queries/useUserCapabilities.js';
import { useIsEnterpriseUser } from '#pplx/frontend/auth/useIsEnterpriseUser.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { memo, useMemo } from 'react';
const SidebarGovBadge = Dynamic(async () => {
    const { SidebarGovBadge } = await import('#pplx/frontend/apps/ask/layout-aux/sidebar-bottom/SidebarGovBadge.js');
    return { default: SidebarGovBadge };
});
const SidebarAccountMenu = Dynamic(async () => {
    const { SidebarAccountMenu } = await import('#pplx/frontend/apps/ask/layout-aux/sidebar-bottom/SidebarAccountMenu.js');
    return { default: SidebarAccountMenu };
});
const SidebarEnterpriseMenu = Dynamic(async () => {
    const { SidebarEnterpriseMenu } = await import('#pplx/frontend/apps/ask/layout-aux/sidebar-bottom/SidebarEnterpriseMenu.js');
    return { default: SidebarEnterpriseMenu };
});
const SidebarInternalSettingsMenu = Dynamic(async () => {
    const { SidebarInternalSettingsMenu } = await import('#pplx/frontend/apps/ask/restricted-feature-debug-helper/index.js');
    return { default: SidebarInternalSettingsMenu };
}, { restricted: true });
export const SidebarDesktop = memo(function SidebarDesktop() {
    const [isSidebarPinned] = useSidebarPinned();
    const isFunctionallyExpanded = isSidebarPinned;
    const { status } = useSession();
    const isLoggedOut = status === 'unauthenticated';
    const { isGovernmentRequestOrigin } = useUserCapabilities();
    const isEnterpriseUser = useIsEnterpriseUser();
    const { canLoadMenu, showInternalDebugItems, hasAccessToProFeatures } = useSidebarInternalSettingsMenu();
    const showUpgradeButton = useShowUpgradeButton();
    const inviteKind = useSidebarInviteKind();
    const showTeamInviteButton = inviteKind === 'team-upsell';
    const showLargeEnterpriseAddTeamButton = inviteKind === 'org-members' && isFunctionallyExpanded;
    const showSidebarNotifications = status === 'authenticated';
    const showInviteButton = !isFunctionallyExpanded && inviteKind === 'org-members';
    const showInviteButtonInEnterpriseRow = isFunctionallyExpanded && inviteKind === 'org-members' && !showLargeEnterpriseAddTeamButton;
    const topButtonGroup = useMemo(() => {
        const items = [
            (isLoggedOut || showUpgradeButton) && !isFunctionallyExpanded && (_jsx(SidebarPlans, { isExpanded: isFunctionallyExpanded }, "plans-button")),
            showInviteButton && (_jsx(SidebarInvite, { kind: "org-members", presentation: "collapsed" }, "invite-button")),
        ].filter(Boolean);
        if (items.length === 0)
            return null;
        return (_jsx("div", { className: "flex w-full flex-col items-center gap-2 mb-1", children: items }, "top-button-group"));
    }, [isLoggedOut, showUpgradeButton, showInviteButton, isFunctionallyExpanded]);
    const showGovBadge = !!isGovernmentRequestOrigin;
    const bottomLabelItems = useMemo(() => [
        {
            item: topButtonGroup,
            show: !!topButtonGroup,
        },
        {
            item: _jsx(SidebarAccountMenu, { isExpanded: isFunctionallyExpanded }, "account-menu"),
            show: true,
        },
        {
            item: (_jsx(SidebarInvite, { kind: "team-upsell", presentation: isFunctionallyExpanded ? 'expanded' : 'collapsed' }, "team-invite-expanded")),
            show: showTeamInviteButton,
        },
        {
            item: (_jsx("div", { children: _jsx(SidebarEnterpriseMenu, { isExpanded: isFunctionallyExpanded, trailingAccessory: showInviteButtonInEnterpriseRow ? (_jsx(SidebarInvite, { kind: "org-members", presentation: "accessory" })) : undefined }) }, "enterprise-menu")),
            show: isEnterpriseUser,
        },
        {
            item: (_jsx(SidebarInvite, { kind: "org-members", presentation: "expanded" }, "enterprise-invite-large")),
            show: showLargeEnterpriseAddTeamButton,
        },
        {
            item: (_jsx(SidebarInternalSettingsMenu, { isExpanded: isFunctionallyExpanded, showInternalDebugItems: showInternalDebugItems, hasAccessToProFeatures: hasAccessToProFeatures }, "internal-settings")),
            show: canLoadMenu,
        },
        {
            item: _jsx(SidebarGovBadge, {}, "gov-badge"),
            show: showGovBadge,
        },
    ]
        .filter(({ show }) => show)
        .map(({ item }) => item), [
        topButtonGroup,
        showGovBadge,
        isEnterpriseUser,
        showInternalDebugItems,
        canLoadMenu,
        hasAccessToProFeatures,
        isFunctionallyExpanded,
        showInviteButtonInEnterpriseRow,
        showLargeEnterpriseAddTeamButton,
        showTeamInviteButton,
    ]);
    return (_jsx(SidebarDesktopComponent, { bottomLabelItems: bottomLabelItems, showNotifications: showSidebarNotifications, children: (isLoggedOut || showUpgradeButton) && _jsx(SidebarPlans, { isExpanded: isFunctionallyExpanded }) }));
});
//# sourceMappingURL=SidebarDesktopContainer.js.map